<template>
  <div class="c-toc">
    <ul v-if="toc.length > 0">
      <h2 class="c-toc__book-title">{{ bookTitle }}</h2>
      <li v-for="(link, i) in toc" class="c-toc__list-item" :key="link.id">
        <router-link
          v-if="!link.chapters"
          class="c-toc__link"
          :to="{
            params: {
              page: link.pages[0] ? getPageByIndex(link.pages[0].position) : '',
            },
          }"
          @click.native="closeModal()"
        >
          {{ link.name }}
        </router-link>

        <div v-else>
          <router-link
            :ref="`link-${i}`"
            :to="{
              params: {
                page: link.pages[0]
                  ? getPageByIndex(link.pages[0].position)
                  : '',
              },
            }"
            class="c-toc__list-dropdown"
            @click.native="
              link.chapters.length > 0
                ? handleDropDown(`dropdown${i}`, `arrow-${i}`, `link-${i}`)
                : ''
            "
            :aria-label="`${$t('TOGGLE_SUBCHAPTERS')}`"
            aria-expanded="false"
          >
            <h3 class="c-toc__list-dropdown__title">{{ link.name }}</h3>
            <arrow
              class="c-toc__list-dropdown__arrow"
              :ref="`arrow-${i}`"
            ></arrow>
          </router-link>

          <ul
            :ref="`dropdown${i}`"
            class="c-sub-chapters__list c-sub-chapters__list--hide"
          >
            <li
              v-for="item in link.chapters"
              :key="item.id"
              class="c-sub-chapters__item"
            >
              <router-link
                class="c-sub-chapters__title"
                :to="{
                  params: {
                    page: item.pages[0]
                      ? getPageByIndex(item.pages[0].position)
                      : '',
                  },
                }"
                :tag="isHTML ? 'h3' : 'a'"
                @click.native="closeModal()"
              >
                {{ item.name }}
              </router-link>

              <ul class="c-pages__list" v-if="isHTML">
                <li
                  v-for="page in item.pages"
                  :key="page.id"
                  class="c-pages__item"
                >
                  <router-link
                    :to="{ params: { page: getPageByIndex(page.position) } }"
                    class="c-pages__title"
                    @click.native="closeModal()"
                  >
                    {{ page.label }}
                    <span
                      v-if="page.data && page.data.level"
                      class="c-pages__level"
                      :class="`c-pages__level--${page.data.level}`"
                    >
                      <level-star
                        v-if="page.data.level === 'expert'"
                      ></level-star>
                      {{ generateLevelLabel(page.data.level) }}
                    </span>
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </li>
    </ul>

    <div v-else class="c-toc__empty">
      {{ $t('TOC_NO_CONTENT') }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Arrow from '@/assets/svg/Arrow'
import LevelStar from '@/assets/svg/LevelStar'

import { getPageByIndex, getBookMetadata } from '@/utils/utils'

export default {
  name: 'TOC',

  components: {
    arrow: Arrow,
    'level-star': LevelStar,
  },

  data() {
    return {
      useLevelStar: false,
    }
  },

  computed: {
    ...mapState('book', {
      bookTitle: state => state.book.title,
      toc: state => state.toc,
      isHTML: state =>
        getBookMetadata(state.book, '-dbook-html-content', false),
    }),
  },

  methods: {
    getPageByIndex,
    handleDropDown(dropdown, arrow, link) {
      let expanded = this.$refs[link][0].$el.getAttribute('aria-expanded'),
        isExpanded = expanded == 'true'

      this.$refs[dropdown][0].classList.toggle('c-sub-chapters__list--hide')
      this.$refs[arrow][0].$el.classList.toggle(
        'c-toc__list-dropdown__arrow--rotate',
      )
      this.$refs[link][0].$el.setAttribute('aria-expanded', `${!isExpanded}`)
    },

    generateLevelLabel(level) {
      if (level === 'none') {
        return null
      }

      return level
    },

    ...mapActions({
      closeModal: 'bookMenu/closeModal',
    }),
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';

.c-toc__book-title {
  padding-bottom: 1rem;
  font-size: 1.625rem;
  font-weight: bold;
  color: $dark-blue;
}

.c-toc__link {
  color: $dark-blue;

  @include a11yOutline();
}

.c-toc__list-item {
  display: block;
  font-size: 1em;
  font-weight: 500;
  padding: 1.2em 0;
  border-bottom: 0.05em solid $dark-blue-grey;

  &:last-child {
    border-bottom: none;
  }

  &:first-child {
    padding-top: 0;
  }
}

.c-toc__list-dropdown__arrow {
  transform-origin: 50% 75%;
  transform: rotate(-90deg);
  position: relative;
  bottom: 6px;
}

.c-toc__list-dropdown__arrow--rotate {
  transform: rotate(180deg);
}

.c-toc__list-dropdown {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  @include a11yOutline();

  .c-toc__list-dropdown__title {
    color: $dark-blue;
    font-weight: 500;
    font-size: 1em;
  }
}

.c-sub-chapters__item {
  margin-top: 0.7em;
}

.c-sub-chapters__list {
  padding-left: 1.2em;
  margin-top: 2em;
}

.c-sub-chapters__title {
  color: $dark-blue;
  font-weight: 700;
  font-size: 1em;

  @include a11yOutline();
}

.c-sub-chapters__list--hide {
  display: none;
}

.c-pages__list {
  padding-left: 1.2em;
  padding-top: 0.3em;
}

.c-pages__item {
  margin-top: 0.5em;
}

.c-pages__title {
  color: $dark-blue;
  font-weight: 400;

  @include a11yOutline();
}

.c-pages__level {
  margin-left: 0.2em;
  padding: 0 0.4em;
  border-radius: 0.4em;
  font-size: 0.65em;
  text-transform: uppercase;
  display: inline-block;
}

.c-pages__level--medium {
  background-color: $yellow-level;
  color: $black;

  &:before {
    content: '';
    display: inline-block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 8px solid $white;
    margin-right: 0.3em;
  }
}

.c-pages__level--easy {
  background-color: $green-level;
  color: $white;

  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $white;
    margin-right: 0.3em;
  }
}

.c-pages__level--hard {
  background-color: $red-level;
  color: $white;

  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: $white;
    margin-right: 0.3em;
  }
}

.c-pages__level--expert {
  background-color: $black;
  color: $white;

  .svg-level-star {
    padding-top: 0.25em;
  }
}

.c-toc__empty {
  font-size: 0.875em;
  font-weight: 400;
  color: $black;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  margin: 0 auto;
  left: 0;
  right: 0;
  text-align: center;
}
</style>
